// CO 237 — Agent Readiness Check, re-homed from comai-portal's gateway-app onto
// the federation portal-app. The primary public CTA: a free scan of any store
// URL against the ten surfaces agents need. Stateless — the scan runs and the
// result renders inline on this same page (no shareable permalink, share image,
// or email capture; those required persistence we dropped).
import { useState, type FormEvent } from "react";
import { PAGE_HEROES } from "../content/page-copy";
import { motion } from "framer-motion";
import { Container, Section, fadeUp, stagger } from "@comai-portal/design-system";
import {
  scanAgentReady,
  type CheckId,
  type CheckResult,
  type ScanResult,
} from "../services/agentReady";

interface CheckMeta {
  id: CheckId;
  title: string;
  section: "Agent protocols" | "Structured data" | "APIs" | "Access";
}

const CHECK_ORDER: CheckMeta[] = [
  { id: "mcp", title: "MCP endpoint", section: "Agent protocols" },
  { id: "ucp", title: "UCP manifest", section: "Agent protocols" },
  {
    id: "commerceProtocol",
    title: "Agent commerce protocol",
    section: "Agent protocols",
  },
  { id: "catalog", title: "Catalog discoverability", section: "Structured data" },
  {
    id: "productSchema",
    title: "Product schema (JSON-LD)",
    section: "Structured data",
  },
  { id: "policy", title: "Policy / returns surface", section: "Structured data" },
  { id: "inventory", title: "Real-time inventory", section: "APIs" },
  { id: "cartApi", title: "Cart API", section: "APIs" },
  { id: "searchApi", title: "Search API", section: "APIs" },
  { id: "robots", title: "Robots / agent access", section: "Access" },
];

const SECTIONS: Array<CheckMeta["section"]> = [
  "Agent protocols",
  "Structured data",
  "APIs",
  "Access",
];

const HERO = PAGE_HEROES['agent-ready']!;

export function AgentReadyPage() {
  const [url, setUrl] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<ScanResult | null>(null);

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    if (!url.trim()) {
      setError("Please enter a store URL.");
      return;
    }
    setSubmitting(true);
    setResult(null);
    try {
      const scan = await scanAgentReady(url.trim());
      setResult(scan);
    } catch (err) {
      setError(
        err instanceof Error
          ? err.message
          : "Couldn't run the scan. Please try again.",
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="bg-paper text-ink dark:bg-ink dark:text-paper">
      <Section>
        <Container width="prose" className="text-center">
          <motion.div variants={stagger} initial="hidden" animate="show">
            <motion.p variants={fadeUp} className="ds-eyebrow mb-5">
              {HERO.eyebrow}
            </motion.p>
            <motion.h1
              variants={fadeUp}
              className="font-display font-semibold tracking-[-0.02em] text-[clamp(2rem,5.5vw,3.75rem)] leading-[1.08] text-balance"
            >
              {HERO.h1}
            </motion.h1>
            <motion.p
              variants={fadeUp}
              className="text-lead text-mute dark:text-mute-soft mt-6"
            >
              Enter a store URL. We&rsquo;ll check 10 things AI shopping agents
              need to find, understand, and transact on your store today.
            </motion.p>
          </motion.div>

          <form
            onSubmit={onSubmit}
            className="mt-10 mx-auto w-full max-w-xl rounded-2xl border border-signal/20 bg-signal/[0.04] p-4"
          >
            <div className="flex flex-col gap-3 sm:flex-row">
              <input
                type="text"
                placeholder="yourstore.com"
                value={url}
                onChange={(e) => setUrl(e.target.value)}
                disabled={submitting}
                aria-label="Store URL"
                className="h-12 flex-1 rounded-xl border border-ink/15 dark:border-paper/20 bg-paper dark:bg-ink-soft px-4 text-ink dark:text-paper outline-none focus-visible:ring-2 focus-visible:ring-signal"
              />
              <button
                type="submit"
                disabled={submitting}
                className="inline-flex h-12 min-w-[180px] items-center justify-center gap-2 rounded-pill bg-signal px-6 font-display font-medium text-white transition-all duration-200 hover:bg-signal-bright disabled:opacity-50 disabled:pointer-events-none"
              >
                {submitting ? "Scanning…" : "Check my store"}
              </button>
            </div>
            <p className="mt-3 ml-1 text-left text-sm text-mute dark:text-mute-soft">
              Public read-only scan. No login required.
            </p>
          </form>

          {error && (
            <p
              role="alert"
              className="mt-6 mx-auto max-w-xl rounded-xl border border-red-500/30 bg-red-500/10 px-4 py-3 text-sm text-red-600 dark:text-red-400"
            >
              {error}
            </p>
          )}

          {!result && (
            <div className="mt-14 mx-auto max-w-xl text-left">
              <h2 className="font-display text-lg font-semibold mb-3">
                What we check
              </h2>
              <p className="text-mute dark:text-mute-soft leading-relaxed">
                Agent commerce protocols (MCP, UCP, agent-callable checkout) ·
                Product schema · Catalog discoverability · Live inventory · Cart
                and search APIs · Policy/returns surface · Robots.txt agent
                access.
              </p>
            </div>
          )}
        </Container>
      </Section>

      {result && (
        <Section tint="sunk">
          <Container>
            <ResultView result={result} />
          </Container>
        </Section>
      )}
    </div>
  );
}

function ResultView({ result }: { result: ScanResult }) {
  return (
    <div className="mx-auto max-w-3xl">
      <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
        <div>
          <p className="ds-eyebrow">Agent-Ready Score</p>
          <h2 className="font-display text-3xl font-semibold mt-1 break-all">
            {result.url.replace(/^https?:\/\//, "")}
          </h2>
          <div className="mt-3 flex flex-wrap gap-2">
            <span className="rounded-pill bg-ink/[0.06] dark:bg-paper/10 px-3 py-1 text-xs">
              Platform: {result.platform}
            </span>
            <span className="rounded-pill border border-ink/15 dark:border-paper/20 px-3 py-1 text-xs">
              Scanned {new Date(result.scannedAt).toLocaleDateString()}
            </span>
          </div>
        </div>
        <div className="text-left md:text-right">
          <span className="font-display text-6xl md:text-7xl font-extrabold leading-none">
            {result.total.toFixed(1)}
          </span>
          <span className="font-display text-2xl text-mute dark:text-mute-soft font-medium ml-1">
            / 10
          </span>
        </div>
      </div>

      <SegmentBar checks={result.checks} />

      {result.isSpaFallback && (
        <div className="mt-2 rounded-xl border border-signal/30 bg-signal/[0.06] p-4">
          <p className="font-semibold mb-1">This site renders client-side</p>
          <p className="text-sm text-mute dark:text-mute-soft">
            Every URL on this domain returns the same HTML shell — your store is
            a single-page app that renders content with JavaScript after the
            page loads. Most AI shopping agents read the raw HTML response and
            won&rsquo;t see your products, prices, or checkout. The fixes below
            need to be exposed at real, server-rendered URLs (not just
            client-side routes).
          </p>
        </div>
      )}

      {SECTIONS.map((section) => (
        <div key={section} className="mt-8">
          <h3 className="font-display text-lg font-semibold mb-3">{section}</h3>
          <div className="flex flex-col gap-3">
            {CHECK_ORDER.filter((c) => c.section === section).map((meta) => (
              <CheckRow
                key={meta.id}
                title={meta.title}
                result={result.checks[meta.id]}
              />
            ))}
          </div>
        </div>
      ))}

      {/* CO 257 — the ARC funnel chain: after the scan, the next step is not
          a demo call; it's pointing the visitor's own agent at the network.
          The prompt string is contract-fixed (blueprint §1, channel=arc). */}
      <div className="mt-12 rounded-2xl border border-signal/20 bg-signal/[0.06] p-8 text-center">
        <h3 className="font-display text-xl font-semibold mb-2">
          Your store scored {result.total.toFixed(1)}/10. Now point your agent
          at the network it should be on.
        </h3>
        <p className="text-mute dark:text-mute-soft mb-5 mx-auto max-w-xl">
          Don&rsquo;t take our word for what ComOS is — paste one sentence into
          your own agent and let it evaluate the live surface itself:
        </p>
        <pre className="mx-auto max-w-2xl whitespace-pre-wrap break-words rounded-xl bg-ink/[0.04] dark:bg-paper/[0.06] p-4 text-left font-mono text-sm leading-relaxed mb-5">
          {
            "Fetch https://mcp.comos-federation.com/.well-known/comos-vendor.json?channel=arc, read all of it, and tell me: what is ComOS, what would it cost us, and is it worth connecting to?"
          }
        </pre>
        <a
          href="/ask-your-agent"
          className="inline-flex h-12 items-center justify-center gap-2 rounded-pill bg-signal px-7 font-display font-medium text-white transition-all duration-200 hover:bg-signal-bright"
        >
          Why ask your agent? →
        </a>
      </div>
    </div>
  );
}

function SegmentBar({ checks }: { checks: ScanResult["checks"] }) {
  return (
    <div className="mt-6 flex w-full gap-1.5">
      {CHECK_ORDER.map((meta) => {
        const score = checks[meta.id].score;
        const cls =
          score === 1
            ? "bg-emerald-500"
            : score === 0.5
              ? "bg-gradient-to-r from-emerald-500 from-50% to-ink/10 to-50% dark:to-paper/10"
              : "bg-ink/10 dark:bg-paper/10";
        return (
          <div
            key={meta.id}
            title={`${meta.title}: ${score}`}
            className={`h-3 flex-1 rounded ${cls}`}
          />
        );
      })}
    </div>
  );
}

function CheckRow({ title, result }: { title: string; result: CheckResult }) {
  const badge =
    result.score === 1
      ? { label: "Pass", cls: "bg-emerald-500/12 text-emerald-600 dark:text-emerald-400" }
      : result.score === 0.5
        ? { label: "Partial", cls: "bg-amber-500/15 text-amber-700 dark:text-amber-400" }
        : { label: "Missing", cls: "bg-ink/[0.06] dark:bg-paper/10 text-mute dark:text-mute-soft" };

  const icon = result.score === 1 ? "✓" : result.score === 0.5 ? "◐" : "✕";

  return (
    <div className="rounded-xl border border-ink/[0.08] dark:border-paper/10 bg-paper dark:bg-ink p-5">
      <div className="flex items-start gap-3">
        <span
          aria-hidden
          className={`pt-0.5 text-lg ${
            result.score === 1
              ? "text-emerald-500"
              : result.score === 0.5
                ? "text-amber-500"
                : "text-mute"
          }`}
        >
          {icon}
        </span>
        <div className="flex-1">
          <div className="mb-1 flex items-center gap-2">
            <span className="font-display font-semibold">{title}</span>
            <span className={`rounded-pill px-2 py-0.5 text-xs font-medium ${badge.cls}`}>
              {badge.label}
            </span>
          </div>
          <p className="text-sm text-mute dark:text-mute-soft mb-1">
            {result.evidence}
          </p>
          {result.score < 1 && (
            <p className="text-sm font-medium">
              <span className="text-signal mr-1">Fix:</span>
              {result.fix}
            </p>
          )}
        </div>
      </div>
    </div>
  );
}

export default AgentReadyPage;
